import he from 'he'
import DOMPurify from 'isomorphic-dompurify'

// Decode HTML entities (e.g. "&amp;" -> "&") in plain-text CMS labels that get
// rendered via Vue's `{{ }}` text interpolation, which does not decode them.
export const decodeEntities = (value?: string | null): string => he.decode(value ?? '')

// Flattens CMS rich text to a single plain-text line, for places that must not
// contain markup (JSON-LD, meta descriptions). Decodes twice on purpose: the CMS
// stores the markup entity-encoded ("&lt;p&gt;"), and the text inside it carries
// its own entities ("&amp;Scaron;"), so one pass leaves half the job done.
export const toPlainText = (value?: string | null, maxLength = 5000): string => {
  const text = he
    .decode(he.decode(value ?? '').replace(/<[^>]+>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim()
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text
}

/**
 * Tags the CMS rich-text editor actually produces (`prose` content): paragraphs,
 * headings, lists, tables, links, images, inline emphasis, and video embeds.
 * Everything else — `<script>`, `<style>`, `<form>`, `<object>`, event handlers —
 * is dropped.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'span', 'div', 'section', 'article', 'header', 'footer',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup', 'small', 'mark', 'abbr', 'blockquote', 'q', 'cite', 'code', 'pre',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'a', 'img', 'figure', 'figcaption', 'picture', 'source',
  'iframe',
]

const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'title',
  'src', 'srcset', 'sizes', 'alt', 'width', 'height', 'loading', 'decoding', 'type', 'media',
  'class', 'style', 'dir', 'lang',
  'colspan', 'rowspan', 'scope', 'align', 'valign',
  'start', 'reversed', 'value',
  'itemprop', 'itemscope', 'itemtype',
  // iframe (video embeds) — the src host is additionally allowlisted below
  'allow', 'allowfullscreen', 'frameborder', 'scrolling',
]

/** Hosts whose `<iframe>` embeds the CMS is allowed to render (video only). */
const IFRAME_HOST_ALLOWLIST = /^(?:https?:)?\/\/(?:[a-z0-9-]+\.)*(?:youtube\.com|youtube-nocookie\.com|youtu\.be|vimeo\.com|player\.vimeo\.com)\//i

let hooksInstalled = false
function installHooks(): void {
  if (hooksInstalled) return
  hooksInstalled = true

  DOMPurify.addHook('uponSanitizeElement', (node: any, data: any) => {
    // An <iframe> survives the tag allowlist only if it points at an allowlisted
    // video host. `javascript:`/`data:`/`srcdoc` iframes are removed outright.
    if (data.tagName !== 'iframe') return
    const src = node.getAttribute?.('src') ?? ''
    if (!IFRAME_HOST_ALLOWLIST.test(src)) node.remove?.()
  })

  DOMPurify.addHook('afterSanitizeAttributes', (node: any) => {
    // Any link the CMS opens in a new tab must not hand the opener over.
    if (node.tagName === 'A' && node.getAttribute?.('target')) {
      node.setAttribute('rel', 'noopener noreferrer')
    }
    // Preserved from the previous implementation: CMS wrapper <div>s carry layout
    // classes from the legacy frontend that fight the app's own styling.
    if (node.tagName === 'DIV') node.removeAttribute?.('class')
  })
}

/**
 * Sanitises CMS-authored HTML for `v-html` / `innerHTML`.
 *
 * The CMS stores its markup entity-encoded, so it must be decoded first — but that
 * decode is what turns an entity-escaped payload back into live markup, so the
 * DOMPurify pass afterwards is what actually makes the result safe. Never render
 * CMS HTML without going through this function (or `<SafeHtml>`).
 */
export const sanitizeHtml = (value: string): string => {
  const decoded = he.decode(value ?? '')

  // Normalize relative image paths from CMS
  const normalized = decoded.replace(/(?:\.\.\/)+images\//g, '/images/')

  installHooks()

  return DOMPurify.sanitize(normalized, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // `srcdoc` is a script-execution vector; `data:` URIs are not needed by the CMS.
    FORBID_ATTR: ['srcdoc'],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: true,
    // No USE_PROFILES here: DOMPurify lets it OVERRIDE ALLOWED_TAGS/ALLOWED_ATTR,
    // which silently turns both allowlists above into dead config (and drops the
    // CMS's video embeds, since `iframe` is not in the html profile).
  })
}

export const removeEmptyTags = (html: string): string => {
  let result = html
  let prev = ''
  while (result !== prev) {
    prev = result
    result = result.replace(/<(\w+)[^>]*>\s*<\/\1>/g, '')
  }
  return result.trim()
}
